import { ensureUrlScheme, type UsageEvent } from '@heybesafe/shared';
import type { AnomalyFlag } from './usageEventAnalysis';

function LockPlaceholder() {
  return (
    <span title="Encrypted — sign in to decrypt" className="text-hbs-text-mute">
      🔒
    </span>
  );
}

function ActionBadge({ action }: { action: UsageEvent['action'] }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
      action === 'copy' || action === 'totp_copy'
        ? 'bg-blue-500/15 text-blue-400'
        : 'bg-purple-500/15 text-purple-400'
    }`}>
      {action}
    </span>
  );
}

function FlagBadge({ flags }: { flags: AnomalyFlag[] }) {
  const labels = flags.map((f) => {
    if (f.kind === 'off-hours') return 'off-hours';
    if (f.kind === 'burst') return 'burst';
    if (f.kind === 'first-item') return 'first-ever access';
    if (f.kind === 'new-platform') return `new ${f.platform}`;
    return 'flagged';
  });
  return (
    <span
      aria-label={`flagged: ${labels.join(', ')}`}
      className="ml-1.5 text-xs"
      style={{ color: '#ef4444' }}
      title={labels.join(' · ')}
    >
      ⚠
    </span>
  );
}

export function EventsFeedTable({
  events,
  orgNameById,
  plaintextById,
  flagsByEventId,
}: {
  events: UsageEvent[];
  orgNameById: (id: string) => string;
  plaintextById: Map<string, { itemName: string; siteUrl: string } | null>;
  flagsByEventId: Map<string, AnomalyFlag[]>;
}) {
  return (
    <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-hbs-border text-xs text-hbs-text-mute uppercase tracking-wide">
              <th className="px-4 py-3 font-medium">Time</th>
              <th className="px-4 py-3 font-medium">Family</th>
              <th className="px-4 py-3 font-medium">Item</th>
              <th className="px-4 py-3 font-medium">Action</th>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Site</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => {
              const plaintext = plaintextById.get(event.id);
              const flags = flagsByEventId.get(event.id);
              const isFlagged = !!flags && flags.length > 0;
              return (
                <tr
                  key={event.id}
                  className={`border-b border-hbs-border hover:bg-hbs-surface transition-colors ${isFlagged ? 'bg-anomaly' : ''}`}
                >
                  <td className="px-4 py-3 text-xs text-hbs-text-mute whitespace-nowrap">
                    {new Date(event.occurredAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-hbs-text-mute">{orgNameById(event.orgId)}</td>
                  <td className="px-4 py-3 text-sm text-hbs-text font-medium">
                    {plaintext?.itemName ?? <LockPlaceholder />}
                    {isFlagged && <FlagBadge flags={flags!} />}
                  </td>
                  <td className="px-4 py-3"><ActionBadge action={event.action} /></td>
                  <td className="px-4 py-3 text-xs text-hbs-text-mute">{event.userEmail}</td>
                  <td className="px-4 py-3 text-xs text-hbs-text-mute truncate max-w-[180px]">
                    {plaintext?.siteUrl
                      ? (
                        <a href={ensureUrlScheme(plaintext.siteUrl) || undefined} target="_blank" rel="noopener noreferrer" className="text-hbs-primary hover:underline truncate">
                          {plaintext.siteUrl}
                        </a>
                      )
                      : plaintext !== undefined && plaintext !== null
                        ? '—'
                        : <LockPlaceholder />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
